import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';

export default function GalleryManager() {
  const [view, setView] = useState('list');
  const [editTarget, setEditTarget] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/gallery')
      .then(r => setGalleries((r.data || []).filter(g => g.name)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const deleteGallery = async (id) => {
    if (!window.confirm('Delete this gallery?')) return;
    await api.delete(`/gallery/${id}`);
    load();
  };

  if (view !== 'list') {
    return (
      <GalleryForm
        existing={editTarget}
        onDone={() => { setView('list'); setEditTarget(null); load(); }}
        onCancel={() => { setView('list'); setEditTarget(null); }}
      />
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-0">Gallery</h4>
          <p className="text-muted small mb-0">Create photo albums or link Facebook albums</p>
        </div>
        <button className="btn btn-primary rounded-pill px-4" onClick={() => { setEditTarget(null); setView('create'); }}>
          <i className="fas fa-plus me-2"></i>New Gallery
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : galleries.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5 text-muted">
            <i className="fas fa-images fa-3x mb-3 d-block" style={{ opacity: 0.3 }}></i>
            <p className="mb-3">No galleries yet.</p>
            <button className="btn btn-primary rounded-pill px-4" onClick={() => setView('create')}>
              <i className="fas fa-plus me-2"></i>Create First Gallery
            </button>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {galleries.map(g => (
            <div key={g.id} className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="position-relative" style={{ height: 140, overflow: 'hidden', borderRadius: '8px 8px 0 0', background: '#f4f6fb' }}>
                  {g.facebook_url ? (
                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-primary">
                      {g.facebook_cover ? (
                        <img src={g.facebook_cover} alt="" className="w-100 h-100" style={{ objectFit: 'cover', opacity: 0.7 }} />
                      ) : (
                        <i className="fab fa-facebook fa-3x"></i>
                      )}
                      <div className="position-absolute bottom-0 start-0 end-0 p-2" style={{ background: 'rgba(24,119,242,0.85)' }}>
                        <span className="text-white small fw-bold"><i className="fab fa-facebook me-1"></i>Facebook Album</span>
                      </div>
                    </div>
                  ) : (g.images || []).length === 0 ? (
                    <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                      <i className="fas fa-image fa-2x" style={{ opacity: 0.3 }}></i>
                    </div>
                  ) : (
                    <div className="row g-0 h-100">
                      {(g.images || []).slice(0, 4).map((img, i, arr) => (
                        <div key={i} className={`col-${arr.length === 1 ? '12' : '6'}`} style={{ height: arr.length <= 2 ? '140px' : '70px' }}>
                          <img src={img.url} alt="" className="w-100 h-100" style={{ objectFit: 'cover', display: 'block' }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="card-body p-3">
                  <h6 className="fw-bold mb-0">{g.name}</h6>
                  {g.description && <p className="text-muted small mb-0 mt-1">{g.description}</p>}
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <span className="badge badge-soft-primary">
                      {g.facebook_url ? 'Facebook' : `${(g.images || []).length} image${(g.images || []).length !== 1 ? 's' : ''}`}
                    </span>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-primary rounded-pill px-3"
                        onClick={() => { setEditTarget(g); setView('edit'); }}>
                        <i className="fas fa-edit me-1"></i>Edit
                      </button>
                      <button className="btn btn-sm btn-outline-danger rounded-pill px-2" onClick={() => deleteGallery(g.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryForm({ existing, onDone, onCancel }) {
  const isEdit = !!existing;
  const isFacebook = existing?.facebook_url ? true : false;

  const [name, setName] = useState(existing?.name || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [albumType, setAlbumType] = useState(isFacebook ? 'facebook' : 'photos');
  const [facebookUrl, setFacebookUrl] = useState(existing?.facebook_url || '');
  const [facebookCover, setFacebookCover] = useState(existing?.facebook_cover || '');
  const [images, setImages] = useState(existing?.images || []);
  const [newUrl, setNewUrl] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [urlError, setUrlError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showImgPicker, setShowImgPicker] = useState(false);

  const addImage = () => {
    if (!newUrl.trim()) { setUrlError('Please enter a URL.'); return; }
    const url = newUrl.trim();
    if (images.some(i => i.url === url)) { setUrlError('This image is already in the gallery.'); return; }
    setUrlError('');
    setImages(prev => [...prev, { url, caption: newCaption.trim() }]);
    setNewUrl('');
    setNewCaption('');
  };

  const addFromPicker = url => {
    if (!images.some(i => i.url === url)) {
      setImages(prev => [...prev, { url, caption: '' }]);
    }
    setShowImgPicker(false);
  };

  const removeImage = idx => setImages(prev => prev.filter((_, i) => i !== idx));
  const moveUp = idx => {
    if (idx === 0) return;
    setImages(prev => { const a = [...prev]; [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]]; return a; });
  };
  const moveDown = idx => {
    setImages(prev => {
      if (idx === prev.length - 1) return prev;
      const a = [...prev]; [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]]; return a;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError('Gallery name is required.');
    if (albumType === 'facebook' && !facebookUrl.trim()) return setError('Facebook album URL is required.');
    setSaving(true); setError('');
    try {
      const payload = {
        name: name.trim(),
        description,
        images: albumType === 'photos' ? images : [],
        facebook_url: albumType === 'facebook' ? facebookUrl.trim() : '',
        facebook_cover: albumType === 'facebook' ? facebookCover.trim() : '',
      };
      if (isEdit) {
        await api.put(`/gallery/${existing.id}`, payload);
      } else {
        const r = await api.post('/gallery', { name: payload.name, description: payload.description, facebook_url: payload.facebook_url, facebook_cover: payload.facebook_cover });
        const gallery = r.data;
        if (payload.images.length) {
          await api.post(`/gallery/${gallery.id}/images`, { images: payload.images });
        }
      }
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <>
      {showCoverPicker && (
        <S3ImagePicker
          onSelect={url => { setFacebookCover(url); setShowCoverPicker(false); }}
          onClose={() => setShowCoverPicker(false)}
        />
      )}
      {showImgPicker && (
        <S3ImagePicker
          onSelect={addFromPicker}
          onClose={() => setShowImgPicker(false)}
        />
      )}

      <div>
        <div className="d-flex align-items-center gap-3 mb-4">
          <button className="btn btn-outline-secondary rounded-pill px-3" onClick={onCancel}>
            <i className="fas fa-arrow-left me-2"></i>Back
          </button>
          <h4 className="fw-bold mb-0">{isEdit ? `Edit: ${existing.name}` : 'New Gallery'}</h4>
        </div>

        {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

        <form onSubmit={save}>
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label small fw-bold">Gallery Name *</label>
                  <input value={name} onChange={e => setName(e.target.value)} className="form-control" placeholder="e.g. Dashain 2024" required />
                </div>
                <div className="col-md-7">
                  <label className="form-label small fw-bold">Description</label>
                  <input value={description} onChange={e => setDescription(e.target.value)} className="form-control" placeholder="Optional" />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Album Type</label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input className="form-check-input" type="radio" id="typePhotos" name="albumType" value="photos"
                        checked={albumType === 'photos'} onChange={() => setAlbumType('photos')} />
                      <label className="form-check-label" htmlFor="typePhotos"><i className="fas fa-images me-1"></i>Photo Album</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" id="typeFacebook" name="albumType" value="facebook"
                        checked={albumType === 'facebook'} onChange={() => setAlbumType('facebook')} />
                      <label className="form-check-label" htmlFor="typeFacebook"><i className="fab fa-facebook text-primary me-1"></i>Facebook Album</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {albumType === 'facebook' ? (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-1"><i className="fab fa-facebook text-primary me-2"></i>Facebook Album</h5>
                <p className="text-muted small mb-3">
                  Share your Facebook album publicly, then paste the link below. Visitors will be directed to view the album on Facebook.
                </p>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label small fw-bold">Facebook Album URL *</label>
                    <input
                      className="form-control"
                      placeholder="https://www.facebook.com/media/set/?set=a.… or https://www.facebook.com/groups/…"
                      value={facebookUrl}
                      onChange={e => setFacebookUrl(e.target.value)}
                    />
                    <div className="form-text">
                      On Facebook: open the album → copy the page URL from your browser address bar.
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold">Cover Image <span className="text-muted fw-normal">(optional — shown as thumbnail)</span></label>
                    <div className="d-flex gap-2 align-items-start">
                      <input
                        className="form-control"
                        placeholder="Paste an image URL or pick from media…"
                        value={facebookCover}
                        onChange={e => setFacebookCover(e.target.value)}
                      />
                      <button type="button" className="btn btn-outline-secondary rounded-pill px-3 text-nowrap" onClick={() => setShowCoverPicker(true)}>
                        <i className="fas fa-folder-open me-1"></i>Media
                      </button>
                    </div>
                    {facebookCover && (
                      <div className="mt-2">
                        <img src={facebookCover} alt="cover preview" style={{ height: 80, width: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-1">Add Images</h5>
                  <p className="text-muted small mb-3">
                    Paste any direct image URL, or pick from your uploaded media library.
                  </p>
                  <div className="row g-2 align-items-start">
                    <div className="col-12 col-md-6">
                      <input
                        className={`form-control ${urlError ? 'is-invalid' : ''}`}
                        placeholder="https://… direct image URL"
                        value={newUrl}
                        onChange={e => { setNewUrl(e.target.value); setUrlError(''); }}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addImage())}
                      />
                      {urlError && <div className="invalid-feedback">{urlError}</div>}
                    </div>
                    <div className="col-12 col-md-3">
                      <input
                        className="form-control"
                        placeholder="Caption (optional)"
                        value={newCaption}
                        onChange={e => setNewCaption(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addImage())}
                      />
                    </div>
                    <div className="col-6 col-md-auto">
                      <button type="button" className="btn btn-outline-primary rounded-pill w-100" onClick={addImage}>
                        <i className="fas fa-plus me-1"></i>Add URL
                      </button>
                    </div>
                    <div className="col-6 col-md-auto">
                      <button type="button" className="btn btn-outline-secondary rounded-pill w-100" onClick={() => setShowImgPicker(true)}>
                        <i className="fas fa-folder-open me-1"></i>Media
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {images.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <h6 className="fw-bold mb-3">Images ({images.length})</h6>
                    <div className="d-flex flex-column gap-2">
                      {images.map((img, idx) => (
                        <div key={idx} className="d-flex align-items-center gap-3 p-2 border rounded bg-light">
                          <img
                            src={img.url} alt=""
                            className="rounded flex-shrink-0"
                            style={{ width: 56, height: 56, objectFit: 'cover', background: '#ddd' }}
                            onError={e => { e.target.style.opacity = '0.3'; }}
                          />
                          <div className="flex-grow-1 min-w-0">
                            <div className="text-muted small text-truncate" style={{ maxWidth: 360 }}>{img.url}</div>
                            {img.caption && <div className="small fw-semibold mt-1">{img.caption}</div>}
                          </div>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <button type="button" className="btn btn-sm btn-outline-secondary px-2" title="Move up"
                              disabled={idx === 0} onClick={() => moveUp(idx)}>
                              <i className="fas fa-arrow-up"></i>
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary px-2" title="Move down"
                              disabled={idx === images.length - 1} onClick={() => moveDown(idx)}>
                              <i className="fas fa-arrow-down"></i>
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger px-2" onClick={() => removeImage(idx)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="d-flex gap-3">
            <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Gallery'}
            </button>
            <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
